/**
 * «Найти номер» на пульте (24.09.2026, решение Evgeniy).
 *
 * Таня: «перезвоны улетают, и их потом не найти». Пульт показывал только то,
 * что на руках; где номер сейчас — у робота, в общей очереди, закрыт — узнать
 * было негде, а контакты в админке диспетчеру закрыты. Здесь: ввёл номер как
 * помнит — видишь, где он и почему, и свою историю по нему.
 *
 * Свой перезвон отсюда забирается той же кнопкой, что в блоке «Перезвоны»
 * (POST /desk/callbacks/:id/take): второго пути выдачи нет. Номер другого
 * диспетчера вернуть может только администратор — кнопки для этого здесь нет
 * намеренно. Номер у робота, в общей очереди или закрытый (24.09.2026, «Свой
 * номер») диспетчер может набрать сама в «Ручном наборе» — подсказка об этом.
 *
 * Номер в запрос уходит уже приведённым к +7…: так он маскируется в логах
 * сервера, а «8 928 256-09-56» в строке запроса лёг бы в журнал целиком.
 * Авторитет нормализации всё равно у сервера (§6.8) — он приводит номер ещё раз.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Phone, Search, X } from 'lucide-react';
import { normalizeRuPhone, type DeskAssignment, type DeskPhoneLookupResponse } from '@cc/shared';

import { api } from '../../shared/api/client.js';
import { formatDuration } from '../../shared/lib/format-duration.js';
import { formatPhone } from '../../shared/lib/format-phone.js';
import { formatDateTimeMsk, formatWhenMsk } from '../../shared/lib/msk.js';
import { Button } from '../../shared/ui/button.js';
import { ErrorNotice } from '../../shared/ui/error-notice.js';

/** Ключ поиска: после «Позвонить сейчас» пульт перечитывает найденное по нему */
export const PHONE_LOOKUP_KEY = ['desk', 'phone-lookup'] as const;

const INPUT_ERROR = 'Введите номер: 10–11 цифр';

export interface PhoneLookupProps {
  /** Наблюдение за чужим пультом: ищем глазами этого диспетчера */
  observedId: string | null;
  /** Забрать свой перезвон карточкой и набрать; нет колбэка — кнопки нет (наблюдение) */
  onTake?: ((queueItemId: string) => void) | undefined;
  /** Перезвон, который сейчас забирается: его кнопка показывает «Набираю…» */
  takingId?: string | null | undefined;
  /** Ошибка последней попытки забрать: что случилось и что делать */
  takeError?: string | undefined;
  /** Какой номер не удалось забрать: ошибка общая с «Перезвонами», показываем её только у него */
  takeErrorId?: string | null | undefined;
}

export function PhoneLookup({
  observedId,
  onTake,
  takingId,
  takeError,
  takeErrorId,
}: PhoneLookupProps): React.JSX.Element {
  const [input, setInput] = useState('');
  const [inputError, setInputError] = useState<string | undefined>(undefined);
  /** Номер последнего поиска в E.164; null — карточки результата нет */
  const [phone, setPhone] = useState<string | null>(null);

  const lookupQuery = useQuery({
    queryKey: [...PHONE_LOOKUP_KEY, observedId, phone],
    queryFn: () =>
      api.get<DeskPhoneLookupResponse>('/desk/phone-lookup', {
        query: { phone: phone ?? '', dispatcherId: observedId ?? undefined },
      }),
    enabled: phone !== null,
  });

  const submit = (): void => {
    const normalized = normalizeRuPhone(input.trim());
    if (normalized === null) {
      setInputError(INPUT_ERROR);
      return;
    }
    setInputError(undefined);
    // Тот же номер ещё раз — перечитать: номер мог уйти, пока карточка висела
    if (normalized === phone) void lookupQuery.refetch();
    else setPhone(normalized);
  };

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5">
      <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
        <Search className="h-4 w-4 text-blue-600" aria-hidden />
        Найти номер
      </h2>

      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <input
          aria-label="Номер телефона"
          aria-invalid={inputError !== undefined}
          inputMode="tel"
          autoComplete="off"
          placeholder="8 928 256-09-56"
          value={input}
          onChange={(event) => {
            setInput(event.target.value);
          }}
          className={
            'h-11 min-w-0 flex-1 rounded-lg border bg-white px-3 text-base text-slate-900 ' +
            'focus:outline focus:outline-2 focus:outline-offset-0 focus:outline-blue-500 ' +
            (inputError !== undefined ? 'border-red-500' : 'border-slate-300')
          }
        />
        <Button type="submit" busy={lookupQuery.isFetching}>
          Найти
        </Button>
      </form>

      {inputError !== undefined ? (
        <p role="alert" className="text-sm text-red-700">
          {inputError}
        </p>
      ) : null}

      {phone === null ? null : lookupQuery.isError ? (
        <ErrorNotice
          error={lookupQuery.error}
          action="Номер не проверен — повторите поиск. Не проходит — обновите страницу."
        />
      ) : lookupQuery.data === undefined ? (
        <p className="text-sm text-slate-500">Ищу…</p>
      ) : (
        <PhoneLookupResult
          result={lookupQuery.data}
          onClose={() => {
            setPhone(null);
          }}
          onTake={onTake}
          takingId={takingId}
          takeError={takeError}
          takeErrorId={takeErrorId}
        />
      )}
    </section>
  );
}

export interface PhoneLookupResultProps {
  result: DeskPhoneLookupResponse;
  onClose: () => void;
  onTake?: ((queueItemId: string) => void) | undefined;
  /** Перезвон, который сейчас забирается (из этого блока или из «Перезвонов») */
  takingId?: string | null | undefined;
  takeError?: string | undefined;
  takeErrorId?: string | null | undefined;
}

type Found = Extract<DeskPhoneLookupResponse, { found: true }>;

const HISTORY_STATUS: Record<DeskAssignment['status'], string> = {
  open: 'на пульте, не звонили',
  called: 'на пульте, без метки',
  needs_label: 'на пульте, нужна метка',
  labeled: 'закрыт меткой',
  expired: 'снят по таймауту',
  // Снимает и администратор, и перевод дня роботу — кто именно, здесь не видно
  revoked: 'снят с пульта',
};

/** Карточка найденного номера: где он, почему и что по нему было у диспетчера. */
export function PhoneLookupResult({
  result,
  onClose,
  onTake,
  takingId,
  takeError,
  takeErrorId,
}: PhoneLookupResultProps): React.JSX.Element {
  const now = new Date();

  return (
    <div className="flex flex-col gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
      <div className="flex items-start justify-between gap-2">
        <p className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-semibold tabular-nums text-slate-900">{formatPhone(result.phone)}</span>
          {result.found && result.fullName !== null ? (
            <span className="min-w-0 break-words">{result.fullName}</span>
          ) : null}
        </p>
        <button
          type="button"
          aria-label="Закрыть"
          title="Закрыть"
          onClick={onClose}
          className="shrink-0 rounded-lg p-1 text-slate-500 hover:bg-slate-200"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      {!result.found ? (
        <p className="font-semibold text-slate-900">Такого номера среди ваших нет</p>
      ) : (
        <>
          <p className="font-bold text-slate-900">{headline(result, now)}</p>
          {result.state === 'robot' && result.reason !== null ? (
            <p>Почему: {result.reason}</p>
          ) : null}
          {result.comment !== null ? (
            <p className="break-words whitespace-pre-line opacity-80">
              Договорились: {result.comment}
            </p>
          ) : null}

          {result.state === 'my_callback' && onTake !== undefined ? (
            <button
              type="button"
              disabled={takingId !== null && takingId !== undefined}
              title="Забрать номер карточкой и набрать"
              onClick={() => {
                onTake(result.queueItemId);
              }}
              className="inline-flex items-center gap-1.5 self-start rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              <Phone className="h-4 w-4" aria-hidden />
              {takingId === result.queueItemId ? 'Набираю…' : 'Позвонить сейчас'}
            </button>
          ) : null}
          {result.state === 'my_callback' &&
          takeError !== undefined &&
          takeErrorId === result.queueItemId ? (
            <p className="rounded-xl bg-red-50 px-3 py-2 text-red-800">
              Не удалось забрать номер: {takeError}. Обновите страницу — возможно, карточка уже
              на пульте.
            </p>
          ) : null}
          {dialHint(result) === null ? null : (
            <p className="text-slate-500">{dialHint(result)}</p>
          )}

          {result.history.length > 0 ? (
            <div className="flex flex-col">
              <h3 className="pt-1 font-semibold text-slate-900">История по номеру</h3>
              <ul className="flex flex-col">
                {result.history.map((entry) => (
                  <li
                    key={entry.assignmentId}
                    className="flex flex-col gap-0.5 border-t border-slate-200 py-1.5"
                  >
                    <span>
                      <span className="tabular-nums">{formatDateTimeMsk(entry.assignedAt)}</span>
                      {' · '}
                      <span className="font-semibold">
                        {entry.labelTitle ?? HISTORY_STATUS[entry.status]}
                      </span>
                    </span>
                    {/* Комментарий — только у метки: у снятой карточки там код причины */}
                    {entry.status === 'labeled' && entry.comment !== null ? (
                      <span className="break-words whitespace-pre-line opacity-80">
                        {entry.comment}
                      </span>
                    ) : null}
                    <span className="text-slate-500 tabular-nums">
                      Звонков: {entry.callsCnt}, разговор {formatDuration(entry.talkSec)}
                      {entry.nextActionAt !== null
                        ? `, перезвон на ${formatWhenMsk(entry.nextActionAt, now)}`
                        : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

/** Закрыт заявкой: повторную по свежему лиду набор своего номера не даст. */
const LEAD_CLOSED_REASONS = new Set(['заявка', 'робот оформил заявку']);

/** Что диспетчер может сделать с номером сама; null — подсказка не нужна. */
function dialHint(result: Found): string | null {
  switch (result.state) {
    case 'other':
      return 'Вернуть номер может администратор';
    case 'my_next_round':
      return 'Наберите в «Ручном наборе» — откроется карточкой, лимит своих номеров не тратится';
    case 'robot':
    case 'pool':
      return 'Можно набрать самой в «Ручном наборе» — откроется карточкой, займёт 1 свой номер из дневного лимита';
    case 'closed':
      return result.reason !== null && LEAD_CLOSED_REASONS.has(result.reason)
        ? 'Вернуть номер может администратор'
        : 'Можно набрать самой в «Ручном наборе» — откроется карточкой, займёт 1 свой номер. Если клиент отказался навсегда, пульт не даст набрать';
    case 'my_callback':
    case 'my_card':
      return null;
  }
}

/** Где номер сейчас — одной строкой; время в МСК, с датой для не сегодняшнего дня. */
function headline(result: Found, now: Date): string {
  const when = result.at === null ? null : formatWhenMsk(result.at, now);
  switch (result.state) {
    case 'my_callback':
      return when === null ? 'Перезвон у вас' : `Перезвон у вас: ${when}`;
    case 'my_card':
      return 'Карточка у вас на пульте';
    case 'my_next_round':
      return when === null ? 'Ждёт следующего круга у вас' : `Ждёт следующего круга у вас, с ${when}`;
    case 'other':
      return 'У другого диспетчера';
    case 'pool':
      return 'В общей очереди';
    case 'robot':
      return when === null ? 'У робота' : `У робота с ${when}`;
    case 'closed':
      return `Закрыт: ${result.reason ?? 'снят с прозвона'}${when === null ? '' : `, ${when}`}`;
  }
}
